import { logErrorEvent } from '@/common/util/logEvent';
import { createPage, If, IntrinsicProps } from '@/common/util/templateHelpers';
import EmbedLayout from '@/layouts/EmbedLayout';
import { Game } from '@/util/formatters';
import { useEffect, useRef, useState } from 'react';
import gameApi from '@/apis/drupal/game';

import style from './index.module.scss';
import { AdContainer, AdContainerSize, Flex, LoaderFill } from '@/common/components';
import Yolla, { YollaType, YollaSubType } from '@/components/Yolla';
import { Meta } from '@/common/components/head';
import { displayPreroll, tryPreroll } from '@/util/yollaPreroll';
import AdSandwich from '@/components/AdSandwich';

const DEVICE_QUERY = 'only screen and (max-device-width: 960px)';

interface EmbedPageProps extends IntrinsicProps {
  game: Game
}

function notFound (path: string, error: any, fatal = false) {
  logErrorEvent(`Embed Page Error - ${path}`, fatal, error);

  return {
    notFound: true,
    revalidate: process.env.APP_CONFIG.REVALIDATE_INTERVAL_MEDIUM
  };
}

export async function getServerSideProps ({ params }) {
  const asPath = `/embed/html5-games/${params.nid}`;

  const game = await gameApi.getByNid(params.nid);
  if (game.error) return notFound(asPath, new Error('Could not fetch game by nid.'));
  
  if (!game.canEmbed) return {
    notFound: true,
    revalidate: process.env.APP_CONFIG.REVALIDATE_INTERVAL_MEDIUM
  };

  const props: EmbedPageProps = {
    game
  };

  return {
    props,
  };
}

export default createPage<EmbedPageProps>('EmbedPage', 
  { style }, 
  function EmbedPage ({ className }, props) {
    const [ ready, setReady ] = useState(false);

    {
      const game = props.game;
      useEffect(() => {
        if (!ready) return;
        if (!game) return;
        
        // Track game view
        gameApi.getById(game.uid, true);

        // Ready
        setReady(true);

      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [ ready ]);
    }

    return (
      <EmbedLayout>
        <Meta 
          title='Embed page'
          titleTag='Embed page | Addicting Games'
          description='Play thousands of free online games: arcade games, puzzle games, funny games, sports games, shooting games, and more. New free games every day at AddictingGames.'
          keywords='arcade games, puzzle games, funny games, sports games, shooting games'
        />

        <AdSandwich fullScreen className={className}>
          {
            If(!props.game, () => (
              <LoaderFill />
            ))
              .Else(() => (
                <object id='game-content-frame' data={props.game.embedUrl} />
              ))
              .EndIf()
          }
        </AdSandwich>
      </EmbedLayout>
    );
  });
